export type EvidenceType = {
  id: string;
  name: string;
  desc: string;
  icon: string;
  count: string;
  image: string;
};

export type EvidenceItem = {
  id: string;
  event: string;
  type: string;
  typeLabel: string;
  cover: string;
  source: string;
  timestamp: string;
  location: string;
  summary: string;
  tags: string[];
  featured?: boolean;
};

export type ShortNotice = {
  id: string;
  time: string;
  title: string;
  tag: string;
};

export type TimelineStep = {
  id: string;
  time: string;
  title: string;
  desc: string;
};

export type FaqItem = {
  id: string;
  q: string;
  a: string;
};

export const evidenceTypes: EvidenceType[] = [
  {
    id: "chat",
    name: "聊天截图",
    desc: "私信、群聊与对话记录画面，重点核对头像、昵称与时间顺序是否连贯。",
    icon: "ri-chat-3-line",
    count: "1,286 组",
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20stylized%20chat%20interface%20cards%20floating%20on%20warm%20cream%20paper%20background%2C%20minimal%20speech%20bubbles%2C%20soft%20amber%20light%2C%20editorial%20minimal%20composition%2C%20muted%20vermillion%20accent%2C%20high%20detail&width=640&height=640&seq=hl-type-chat-01&orientation=squarish",
  },
  {
    id: "together",
    name: "同框照",
    desc: "多人合影与并排画面，关注光线方向、边缘裁切与人物站位是否自然。",
    icon: "ri-group-2-line",
    count: "934 张",
    image:
      "https://readdy.ai/api/search-image?query=Two%20empty%20vintage%20photo%20frames%20side%20by%20side%20on%20warm%20textured%20paper%2C%20editorial%20minimal%20flat%20lay%2C%20soft%20natural%20light%2C%20amber%20and%20sepia%20tones%2C%20clean%20simple%20background%2C%20high%20detail&width=640&height=640&seq=hl-type-together-01&orientation=squarish",
  },
  {
    id: "location",
    name: "定位信息",
    desc: "地图标点、行程记录与地理水印，用于交叉比对同框照的拍摄地点。",
    icon: "ri-map-pin-2-line",
    count: "612 条",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20folded%20map%20with%20a%20single%20location%20pin%20marker%20on%20warm%20kraft%20paper%2C%20editorial%20flat%20lay%2C%20soft%20golden%20light%2C%20muted%20tones%2C%20clean%20minimal%20composition%2C%20high%20detail&width=640&height=640&seq=hl-type-location-01&orientation=squarish",
  },
  {
    id: "timestamp",
    name: "时间戳画面",
    desc: "系统时钟、日历页与镜头水印，是判断事件先后顺序的关键依据。",
    icon: "ri-time-line",
    count: "1,047 条",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20vintage%20clock%20and%20calendar%20pages%20on%20warm%20textured%20paper%20surface%2C%20editorial%20flat%20lay%2C%20soft%20amber%20light%2C%20sepia%20muted%20tones%2C%20clean%20minimal%20background%2C%20high%20detail&width=640&height=640&seq=hl-type-timestamp-01&orientation=squarish",
  },
];

export const evidenceItems: EvidenceItem[] = [
  {
    id: "ev-01",
    event: "跨年商圈合影事件",
    type: "together",
    typeLabel: "同框照",
    cover:
      "https://readdy.ai/api/search-image?query=Close%20up%20stylized%20evidence%20board%20with%20overlapping%20printed%20photographs%20pinned%20on%20cork%2C%20red%20string%20connections%2C%20warm%20amber%20side%20light%2C%20documentary%20editorial%20style%2C%20muted%20tones%2C%20simple%20clean%20composition%2C%20high%20detail&width=900&height=680&seq=hl-flow-board-01&orientation=landscape",
    source: "公开社交平台存档",
    timestamp: "2024-12-31 23:14",
    location: "商圈 · 南广场",
    summary:
      "同框照中三人站位与背景店招可作为定位依据，配合当晚定位截图可还原大致行动路线。",
    tags: ["合影", "定位交叉", "夜景"],
    featured: true,
  },
  {
    id: "ev-02",
    event: "直播后台对话存档",
    type: "chat",
    typeLabel: "聊天截图",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20stack%20of%20printed%20photographs%20and%20a%20magnifying%20glass%20on%20warm%20paper%20desk%2C%20editorial%20flat%20lay%2C%20soft%20golden%20light%2C%20sepia%20muted%20palette%2C%20clean%20simple%20background%2C%20high%20detail&width=900&height=680&seq=hl-flow-chat-02&orientation=landscape",
    source: "匿名投稿整理",
    timestamp: "2025-01-08 09:02",
    location: "未标注",
    summary:
      "对话时间轴存在跳档，建议对照系统时间戳画面核对，避免把补发的消息误读为原始记录。",
    tags: ["对话记录", "时间轴", "待核"],
    featured: true,
  },
  {
    id: "ev-03",
    event: "品牌发布会后台照",
    type: "timestamp",
    typeLabel: "时间戳画面",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20sealed%20kraft%20envelope%20with%20red%20wax%20stamp%20and%20documents%20on%20warm%20textured%20desk%2C%20editorial%20flat%20lay%2C%20soft%20amber%20light%2C%20muted%20tones%2C%20minimal%20clean%20background%2C%20high%20detail&width=900&height=680&seq=hl-flow-timestamp-03&orientation=landscape",
    source: "现场设备水印",
    timestamp: "2025-02-16 18:47",
    location: "会展中心 · B 馆",
    summary:
      "镜头水印与场馆电子屏时间一致，属于可信度较高的一类，可与同批次其他画面互相印证。",
    tags: ["水印", "逐帧核对", "可信度高"],
  },
  {
    id: "ev-04",
    event: "跨城行程定位合集",
    type: "location",
    typeLabel: "定位信息",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20topographic%20map%20with%20red%20route%20line%20and%20pin%20markers%2C%20warm%20sepia%20tone%20editorial%20style%2C%20soft%20light%2C%20muted%20palette%2C%20clean%20minimal%20composition%2C%20high%20detail&width=900&height=680&seq=hl-flow-map-04&orientation=landscape",
    source: "行程记录导出",
    timestamp: "2025-03-02 07:35",
    location: "两城往返 · 高铁",
    summary:
      "定位点之间的移动速度存在异常区间，已单独标注，供比对同时段的同框照使用。",
    tags: ["地理标注", "异常区间", "行程"],
  },
  {
    id: "ev-05",
    event: "深夜粉丝群聊记录",
    type: "chat",
    typeLabel: "聊天截图",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20calendar%20timeline%20with%20pinned%20small%20notes%20and%20thread%20on%20warm%20paper%20wall%2C%20editorial%20documentary%20style%2C%20warm%20amber%20light%2C%20muted%20tones%2C%20minimal%20composition%2C%20high%20detail&width=900&height=680&seq=hl-flow-thread-05&orientation=landscape",
    source: "群成员截图",
    timestamp: "2025-03-19 01:22",
    location: "未标注",
    summary:
      "同一段对话出现两种字体渲染，可能是不同机型截图，需按设备特征分组后再做时间排序。",
    tags: ["群聊", "设备差异", "待核"],
  },
  {
    id: "ev-06",
    event: "拍摄现场花絮照片",
    type: "together",
    typeLabel: "同框照",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20archival%20storage%20box%20with%20labeled%20folders%20and%20photographs%20on%20warm%20paper%20background%2C%20editorial%20flat%20lay%2C%20soft%20amber%20light%2C%20muted%20vermillion%20accent%2C%20clean%20minimal%20composition%2C%20high%20detail&width=900&height=680&seq=hl-flow-archive-06&orientation=landscape",
    source: "工作人员留存",
    timestamp: "2025-04-05 15:08",
    location: "影视基地 · 3 号棚",
    summary:
      "画面边缘有棚内布景标识，可据此确认拍摄地，属于可用于佐证的间接素材。",
    tags: ["花絮", "布景标识", "间接佐证"],
  },
];

export const shortNotices: ShortNotice[] = [
  { id: "n1", time: "10:24", title: "新增 12 张跨年合影画面，已补充定位标注", tag: "更新" },
  { id: "n2", time: "09:47", title: "直播后台对话时间轴复核完成，标注 2 处跳档", tag: "核对" },
  { id: "n3", time: "08:31", title: "发布会后台照确认水印一致，可信度上调", tag: "上调" },
  { id: "n4", time: "昨天", title: "跨城行程出现速度异常区间，已单独归档", tag: "存疑" },
  { id: "n5", time: "昨天", title: "粉丝群聊记录按机型重新分组，等待补充原图", tag: "待补" },
  { id: "n6", time: "前天", title: "花絮照片定位到具体棚号，间接佐证成立", tag: "确认" },
];

export const timelineSteps: TimelineStep[] = [
  {
    id: "s1",
    time: "第 1 步",
    title: "先看来源",
    desc: "每张图都标注原始出处：平台存档、匿名投稿或设备水印。来源越具体，越方便回溯原图。",
  },
  {
    id: "s2",
    time: "第 2 步",
    title: "再对时间戳",
    desc: "把画面里的系统时钟、日历页与对话顺序排到同一条时间轴上，检查是否存在跳档或补发。",
  },
  {
    id: "s3",
    time: "第 3 步",
    title: "交叉定位",
    desc: "用同框照里的店招、布景与定位信息互相比对，确认拍摄地是否与描述一致。",
  },
  {
    id: "s4",
    time: "第 4 步",
    title: "标注可信度",
    desc: "综合来源、时间戳与定位给出高 / 中 / 待核三档标注，方便后续检索时快速筛选。",
  },
];

export const faqs: FaqItem[] = [
  {
    id: "f1",
    q: "这些实锤照片的来源是怎么标注的？",
    a: "每张图都会附上来源栏，区分公开平台存档、匿名投稿与设备水印三类，并尽可能保留原始拍摄或截取时间，方便你自行回溯核对。",
  },
  {
    id: "f2",
    q: "聊天截图怎么看有没有被拼接？",
    a: "先看字体渲染是否统一、头像与气泡的间距是否规律，再对照系统时间戳画面检查时间顺序。若同一段对话出现两种渲染风格，通常会标注为待核。",
  },
  {
    id: "f3",
    q: "同框照和定位信息怎么配合判断？",
    a: "以同框照中的店招、地标或布景作为视觉锚点，再去比对定位点与行程记录，两者能对上时，可信度通常会上调一档。",
  },
  {
    id: "f4",
    q: "时间戳画面一定是可靠的吗？",
    a: "设备水印类的可信度相对较高，但仍需与同批次画面互相印证；单独一张时间戳画面不足以作为结论，建议结合来源与定位一起看。",
  },
  {
    id: "f5",
    q: "怎么快速找到某个事件的实锤图？",
    a: "在上方检索框直接输入事件名或关键词，卡片流会即时筛选；也可以按聊天截图、同框照、定位信息、时间戳画面四类入口分门别类浏览。",
  },
];

export const siteStats = [
  { id: "stat1", value: "3,879", label: "归档图组" },
  { id: "stat2", value: "1,247", label: "标注事件" },
  { id: "stat3", value: "92%", label: "带来源标注" },
  { id: "stat4", value: "24h", label: "更新周期" },
];